import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import type { DateView } from "@mui/x-date-pickers/models";
import { isValid as isValidDate, parse, format as formatDate } from "date-fns";
import plLocale from "date-fns/locale/pl";
import { compose } from "ramda";
import React, { memo, useMemo } from "react";
import { Control, Controller } from "react-hook-form";

import { minDate, maxDate } from "../../utilities/validators";
import { withError } from "../HOC";
import { Tooltip } from "../Tooltip";
import { CommonFormElProps } from "../types";

export type DatePickerProps = {
  readonly control: Control;
  readonly isSubmitted: boolean;
  readonly yearOnly?: boolean;
  readonly tooltip?: string;
  readonly defaultInputPreferUS?: boolean;
} & CommonFormElProps;

const SHAPE_SLASH = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

const parseToEUDate = (s: string) => {
  const d = parse(s, "dd/MM/yyyy", new Date());
  return isValidDate(d) ? d : null;
};

const normalizeSlashToEU = (s: string) => {
  if (!SHAPE_SLASH.test(s)) return s;
  const eu = parseToEUDate(s);
  return eu ? formatDate(eu, "dd/MM/yyyy") : s;
};

export const DatePicker = memo<DatePickerProps>(
  ({
    control,
    defaultValue,
    hasError,
    id,
    isSubmitted,
    required = false,
    label: labelTxt,
    yearOnly = false,
    tooltip,
    defaultInputPreferUS = false,
  }) => {
    const views: readonly DateView[] = yearOnly
      ? (["year"] as const)
      : (["year", "month", "day"] as const);
    const openTo: DateView = yearOnly ? "year" : "day";
    const dateFormat = yearOnly ? "yyyy" : "dd/MM/yyyy";

    const normalizedDefaultValue = useMemo(() => {
      if (yearOnly) return defaultValue;
      if (typeof defaultValue !== "string") return defaultValue;
      const s = defaultValue.trim();
      if (!SHAPE_SLASH.test(s)) return defaultValue;
      return normalizeSlashToEU(s);
    }, [defaultValue, yearOnly]);

    const labelAbove = (
      <label
        htmlFor={id}
        style={{
          display: "block",
          marginBottom: 4,
          fontSize: 18,
          color: "#2d2d2d",
          lineHeight: 1.4,
          fontWeight: 400,
        }}
      >
        <span>{labelTxt}</span>
        {required ? <span style={{ color: "#e7401e" }}> *</span> : null}
        <Tooltip copy={tooltip} />
      </label>
    );

    return (
      <>
        {labelAbove}
        <Controller
          name={id}
          control={control}
          defaultValue={normalizedDefaultValue}
          render={({ onChange, onBlur, value, ref }) => {
            const currentValue =
              typeof value === "string"
                ? (() => {
                    if (yearOnly) {
                      if (/^\d{4}$/.test(value)) {
                        const d = new Date(Number(value), 0, 1);
                        return isValidDate(d) ? d : null;
                      }
                      if (SHAPE_SLASH.test(value)) {
                        const eu = parseToEUDate(value);
                        return eu ?? null;
                      }
                      return null;
                    }
                    if (SHAPE_SLASH.test(value)) {
                      const eu = parseToEUDate(value);
                      if (eu) return eu;
                    }
                    return null;
                  })()
                : (value as Date | null);

            return (
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={plLocale}
              >
                <MUIDatePicker
                  format={dateFormat}
                  value={currentValue || null}
                  minDate={minDate}
                  maxDate={maxDate}
                  views={views}
                  openTo={openTo}
                  onChange={(dateValue) => {
                    if (!dateValue || !isValidDate(dateValue)) {
                      onChange(null);
                    } else if (yearOnly) {
                      const y = dateValue.getFullYear();
                      onChange(`01/01/${y}`);
                    } else {
                      onChange(formatDate(dateValue, "dd/MM/yyyy"));
                    }
                    if (isSubmitted) onBlur();
                  }}
                  onClose={() => onBlur()}
                  slotProps={{
                    textField: {
                      id,
                      name: id,
                      variant: "outlined",
                      inputRef: ref,
                      error: hasError,
                      helperText: "",
                      placeholder: dateFormat.toLowerCase(),
                      InputProps: { notched: false },
                      InputLabelProps: { shrink: false },
                      inputProps: yearOnly
                        ? { readOnly: true, inputMode: "none" }
                        : undefined,
                    },
                  }}
                />
              </LocalizationProvider>
            );
          }}
        />
      </>
    );
  }
);

export const DatePickerWithError = compose(withError)(DatePicker);
export default DatePicker;
